import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { fetchTreatmentBySlug, fetchCollection, getOptimizedImageUrl } from '../lib/cms';
import { getImageAlt } from '../lib/images';
import PriceList from '../components/PriceList';
import GallerySection from '../components/GallerySection';
import EmsSeoSection from '../components/EmsSeoSection';
import FedtfrysningSeoSection from '../components/FedtfrysningSeoSection';
import BodyRollSeoSection from '../components/BodyRollSeoSection';
import SEO from '../components/SEO';
import TreatmentFaq from '../components/TreatmentFaq';
import { PAGE_SEO } from '../lib/seo';

const BOOKING_URL = 'https://cryobybreum.planway.com/';

export default function TreatmentDetail({ fixedSlug }) {
  const { slug } = useParams();
  const currentSlug = fixedSlug || slug;

  const [treatment, setTreatment] = useState(null);
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    async function loadData() {
      setLoading(true);
      const [treatmentData, allPrices] = await Promise.all([
        fetchTreatmentBySlug(currentSlug),
        fetchCollection('price_items'),
      ]);

      if (mounted) {
        if (treatmentData) {
          setTreatment(treatmentData);
          
          // Filter prices matching this treatment
          const matchingPrices = allPrices.filter(p => {
            if (currentSlug === 'fedtfrysning') return p.category === 'Fedtfrysning';
            if (currentSlug === 'ems-traening') return p.category === 'EMS Træning' || p.category === 'VIP Forløb';
            if (currentSlug === 'body-roll') return p.category === 'Body Roll';
            return p.category?.toLowerCase().includes(treatmentData.title.toLowerCase());
          });
          setPrices(matchingPrices);
        }
        setLoading(false);
      }
    }

    loadData();
    return () => {
      mounted = false;
    };
  }, [currentSlug]);

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center py-20">
        <div className="text-center space-y-4">
          <i className="fa-solid fa-circle-notch fa-spin text-3xl text-taupe" aria-hidden="true" />
          <p className="text-sm text-taupe font-medium">Henter behandlingsinformation...</p>
        </div>
      </div>
    );
  }

  if (!treatment) {
    return <Navigate to="/404" replace />;
  }

  const heroImg = (treatment.hero_image && treatment.hero_image[0]) || '';
  const featuredImg = (treatment.featured_image && treatment.featured_image[0]) || heroImg;

  return (
    <div className="animate-in fade-in duration-200">
      {PAGE_SEO[currentSlug] ? (
        <SEO preset={currentSlug} />
      ) : (
        <SEO
          title={`${treatment.title} | Cryo by Breum`}
          description={treatment.short_description || treatment.hero_subtitle || PAGE_SEO.home.description}
          image={featuredImg}
          path={`/behandlinger/${currentSlug}`}
        />
      )}
      {/* 1. HERO SECTION */}
      <section className="relative overflow-hidden pt-12 pb-16 lg:py-24 bg-sand-light/50 border-b border-taupe-border/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumbs */}
          <nav className="flex items-center gap-2 text-xs text-taupe mb-8" aria-label="Brødkrummesti">
            <Link to="/" className="hover:text-espresso transition-colors">
              Forside
            </Link>
            <span className="text-taupe-border">/</span>
            <Link to="/#behandlinger" className="hover:text-espresso transition-colors">
              Behandlinger
            </Link>
            <span className="text-taupe-border">/</span>
            <span className="text-espresso font-medium">{treatment.title}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            {/* Copy */}
            <div className="lg:col-span-7 space-y-6">
              <h1 className="font-sans text-3xl sm:text-5xl lg:text-6xl font-semibold text-espresso uppercase tracking-wider leading-[1.15]">
                {treatment.title}
              </h1>

              {treatment.hero_subtitle && (
                <p className="text-base sm:text-lg text-taupe font-normal leading-relaxed max-w-2xl">
                  {treatment.hero_subtitle}
                </p>
              )}

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2">
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-accent-brown text-white text-xs uppercase tracking-widest font-semibold hover:bg-accent-brown-hover transition-colors shadow-xs"
                >
                  <span>Book tid til {treatment.title}</span>
                  <i className="fa-solid fa-arrow-up-right-from-square text-xs" aria-hidden="true" />
                </a>

                <a
                  href="#priser"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-transparent hover:bg-transparent text-espresso text-xs uppercase tracking-widest font-semibold border border-espresso transition-colors"
                >
                  <span>Se priser</span>
                  <i className="fa-solid fa-arrow-down text-xs text-espresso" aria-hidden="true" />
                </a>
              </div>
            </div>

            {/* Hero Image */}
            <div className="lg:col-span-5">
              <div className="overflow-hidden shadow-xl border border-taupe-border bg-sand aspect-[4/3] sm:aspect-[4/3]">
                {heroImg && (
                  <img
                    src={getOptimizedImageUrl(heroImg, { width: 900, quality: 90 })}
                    alt={getImageAlt(heroImg, `${treatment.title} kropsbehandling hos Cryo by Breum i Odense`)}
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. FORKLARING AF BEHANDLINGEN / SEO SEKTION */}
      {currentSlug === 'ems-traening' ? (
        <EmsSeoSection />
      ) : currentSlug === 'fedtfrysning' ? (
        <FedtfrysningSeoSection />
      ) : currentSlug === 'body-roll' ? (
        <BodyRollSeoSection />
      ) : (
        <>
          {/* Standard forklaring for andre behandlinger */}
          <section className="py-20 bg-cream">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-3xl space-y-6 reveal-on-scroll">
                <h2 className="font-sans text-2xl sm:text-3xl lg:text-4xl font-semibold text-espresso uppercase tracking-wider">
                  Sådan virker {treatment.title}
                </h2>
                <div 
                  className="prose prose-stone text-taupe text-base sm:text-lg leading-relaxed space-y-4"
                  dangerouslySetInnerHTML={{ __html: treatment.content_html }}
                />
              </div>
            </div>
          </section>

          {/* Hvad kunden kan forvente & processen */}
          <section className="py-24 bg-sand border-y border-taupe-border/80">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-taupe-border/80 reveal-on-scroll">
                {/* Hvad kan du forvente */}
                {treatment.expectations_html && (
                  <div className="pb-10 lg:pb-0 lg:pr-12 space-y-6">
                    <h3 className="font-sans text-xl sm:text-2xl font-semibold text-espresso uppercase tracking-wider">
                      Hvad kan du forvente?
                    </h3>
                    <div 
                      className="prose prose-stone text-taupe text-sm sm:text-base leading-relaxed space-y-6 [&_p]:leading-loose [&_h4]:font-sans [&_h4]:text-sm [&_h4]:font-bold [&_h4]:text-espresso [&_h4]:uppercase [&_h4]:tracking-wider [&_h4]:mb-2 [&_h4]:mt-6 [&_h4:first-child]:mt-0"
                      dangerouslySetInnerHTML={{ __html: treatment.expectations_html }}
                    />
                  </div>
                )}

                {/* Sådan foregår forløbet */}
                {treatment.process_html && (
                  <div className="pt-10 lg:pt-0 lg:pl-12 space-y-6">
                    <h3 className="font-sans text-xl sm:text-2xl font-semibold text-espresso uppercase tracking-wider">
                      Sådan foregår en session
                    </h3>
                    <div 
                      className="prose prose-stone text-taupe text-sm sm:text-base leading-relaxed space-y-5 [&_p]:leading-loose"
                      dangerouslySetInnerHTML={{ __html: treatment.process_html }}
                    />
                  </div>
                )}
              </div>
            </div>
          </section>
        </>
      )}

      {/* 4. DYNAMISK CMS-GALLERI TILKNYTTET DENNE BEHANDLING */}
      {treatment.gallery_slug && (
        <GallerySection
          gallerySlug={treatment.gallery_slug}
          title={`Billeder fra ${treatment.title}`}
          subtitle={`Et kig på behandlingsudstyret og atmosfæren hos Cryo by Breum`}
        />
      )}

      {/* 5. OFTE STILLEDE SPØRGSMÅL (FAQ) - Placeret direkte under billederne */}
      <TreatmentFaq slug={currentSlug} treatmentTitle={treatment.title} />

      {/* 6. PRISER FOR DENNE BEHANDLING */}
      <section id="priser" className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16 space-y-3 reveal-on-scroll">
          <h2 className="font-sans text-2xl sm:text-3xl lg:text-4xl font-semibold text-espresso uppercase tracking-wider">
            Priser for {treatment.title}
          </h2>
          <p className="text-taupe text-sm sm:text-base">
            Aktuelle priser og eventuelle tilbud i henhold til klinikkens Planway booking.
          </p>
        </div>

        <div className="reveal-on-scroll delay-150">
          <PriceList items={prices} filterCategory={null} />
        </div>

        <div className="mt-12 text-center">
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-9 py-4 bg-accent-brown text-white text-xs font-semibold uppercase tracking-widest hover:bg-accent-brown-hover transition-colors shadow-sm"
          >
            <span>Gå direkte til Planway booking</span>
            <i className="fa-solid fa-arrow-up-right-from-square text-xs" aria-hidden="true" />
          </a>
        </div>
      </section>

      {/* 7. BOOKING BANNER */}
      <section className="py-16 bg-espresso text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-5">
          <h3 className="font-sans text-xl sm:text-2xl font-semibold tracking-wider uppercase text-cream">
            Ønsker du at prøve {treatment.title}?
          </h3>
          <p className="text-sand/80 text-sm sm:text-base max-w-xl mx-auto">
            Book din tid online i dag eller kontakt{' '}
            <Link to="/" className="text-cream hover:underline font-medium">
              Cryo by Breum
            </Link>{' '}
            på Dalumvej 82 i Odense SV for yderligere information via vores{' '}
            <Link to="/kontakt" className="text-cream hover:underline font-medium">
              kontaktside
            </Link>
            .
          </p>
          <div className="pt-2">
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-cream text-espresso text-xs font-semibold tracking-widest uppercase hover:bg-white transition-colors shadow-sm"
            >
              <span>Book tid på Planway</span>
              <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" aria-hidden="true" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
